// src/components/USDTPayment.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { db } from '../firebase';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';

const USDT_WALLET_ADDRESS = "TYourUSDTWalletAddressHere123456789"; // Replace with your USDT wallet

const SUBSCRIPTION_PRICES = {
  monthly: { amount: 97, label: 'Monthly Plan', duration: '30 days' },
  quarterly: { amount: 247, label: 'Quarterly Plan', duration: '90 days' },
  yearly: { amount: 797, label: 'Yearly Plan', duration: '365 days' }
};

function USDTPayment() {
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [timeLeft, setTimeLeft] = useState(1800); // 30 minutes
  const [transactionHash, setTransactionHash] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();
  
  const { plan, userId, registrationId } = location.state || {};
  const subscription = SUBSCRIPTION_PRICES[plan];

  useEffect(() => {
    if (!plan || !userId || !registrationId) {
      navigate('/register');
      return;
    }

    // Start countdown timer
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setPaymentStatus('expired');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Listen for payment confirmation in Firestore
    const unsubscribe = onSnapshot(
      doc(db, "UserRegistrations", registrationId),
      (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          if (data.paymentStatus === 'confirmed') {
            setPaymentStatus('confirmed');
            clearInterval(timer);
          }
        }
      }
    );

    return () => {
      clearInterval(timer);
      unsubscribe();
    };
  }, [plan, userId, registrationId, navigate]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const verifyTransaction = async () => {
    if (!transactionHash) {
      alert('Please enter the transaction hash');
      return;
    }

    setIsVerifying(true);
    
    try {
      // Here you would integrate with a blockchain API to verify the transaction
      // For now, we'll simulate the verification and update Firestore
      
      // Update payment status in Firestore
      await updateDoc(doc(db, "UserRegistrations", registrationId), {
        paymentStatus: 'verifying',
        transactionHash: transactionHash,
        paymentSubmittedAt: new Date().toISOString()
      });

      // Simulate verification delay
      setTimeout(async () => {
        await updateDoc(doc(db, "UserRegistrations", registrationId), {
          paymentStatus: 'confirmed',
          paymentConfirmedAt: new Date().toISOString(),
          subscriptionStartDate: new Date().toISOString(),
          subscriptionEndDate: new Date(Date.now() + (subscription.duration.split(' ')[0] * 24 * 60 * 60 * 1000)).toISOString()
        });
        
        setPaymentStatus('confirmed');
        setIsVerifying(false);
        
        // Redirect to dashboard after confirmation
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      }, 5000);

    } catch (error) {
      console.error('Error verifying transaction:', error);
      setIsVerifying(false);
      alert('Verification failed. Please try again or contact support.');
    }
  };

  if (!subscription) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white text-center">
          <h2 className="text-2xl font-bold mb-4">Invalid Payment Request</h2>
          <button 
            onClick={() => navigate('/register')}
            className="px-6 py-3 bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Registration
          </button>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'confirmed') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-black/20 backdrop-blur-xl rounded-3xl p-8 border border-green-500/20 text-center max-w-md mx-4"
        >
          <div className="text-6xl mb-4">✅</div>
          <h2 className="text-3xl font-bold text-white mb-4">Payment Confirmed!</h2>
          <p className="text-green-400 mb-6">
            Your {subscription.label} subscription has been activated.
          </p>
          <p className="text-gray-300 mb-6">
            Redirecting to your dashboard...
          </p>
          <div className="animate-spin w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full mx-auto"></div>
        </motion.div>
      </div>
    );
  }

  if (paymentStatus === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-black/20 backdrop-blur-xl rounded-3xl p-8 border border-red-500/20 text-center max-w-md mx-4"
        >
          <div className="text-6xl mb-4">⏰</div>
          <h2 className="text-3xl font-bold text-white mb-4">Payment Expired</h2>
          <p className="text-red-400 mb-6">
            The payment window has expired. Please try again.
          </p>
          <button 
            onClick={() => navigate('/register')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Start New Registration
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">

      <div className="relative container mx-auto p-4 flex flex-col items-center justify-center min-h-screen">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-black/20 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl max-w-lg w-full"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-2 rounded-full text-white font-semibold mb-4">
              <span>💎</span>
              {subscription.label}
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Complete Payment</h1>
            <p className="text-gray-300">Send USDT to activate your subscription</p>
          </div>

          {/* Timer */}
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6 text-center">
            <p className="text-red-400 text-sm mb-2">Payment expires in:</p>
            <div className="text-3xl font-bold text-red-400 font-mono">
              {formatTime(timeLeft)}
            </div>
          </div>

          {/* Payment Details */}
          <div className="space-y-6">
            {/* Amount */}
            <div className="bg-gray-800/30 rounded-xl p-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Amount to Send:</span>
                <div className="text-right">
                  <div className="text-2xl font-bold text-green-400">${subscription.amount} USDT</div>
                  <div className="text-sm text-gray-400">TRC-20 Network</div>
                </div>
              </div>
            </div>

            {/* QR Code */}
            <div className="bg-white p-6 rounded-xl">
              <div className="flex justify-center">
                <QRCodeSVG 
                  value={USDT_WALLET_ADDRESS} 
                  size={200}
                  bgColor="#ffffff"
                  fgColor="#000000"
                  includeMargin={true}
                />
              </div>
            </div>

            {/* Wallet Address */}
            <div className="bg-gray-800/30 rounded-xl p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-400">USDT Wallet Address:</span>
                <button
                  onClick={() => copyToClipboard(USDT_WALLET_ADDRESS)}
                  className="text-blue-400 hover:text-blue-300 transition-colors"
                >
                  {copySuccess ? '✅ Copied!' : '📋 Copy'}
                </button>
              </div>
              <div className="bg-gray-900/50 p-3 rounded-lg">
                <code className="text-green-400 text-sm break-all font-mono">
                  {USDT_WALLET_ADDRESS}
                </code>
              </div>
            </div>

            {/* Transaction Hash Input */}
            <div className="space-y-4">
              <div>
                <label className="block text-gray-400 text-sm mb-2">
                  Transaction Hash (after sending):
                </label>
                <input
                  type="text"
                  value={transactionHash}
                  onChange={(e) => setTransactionHash(e.target.value)}
                  placeholder="Enter transaction hash to verify payment"
                  className="w-full bg-gray-800/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={verifyTransaction}
                disabled={!transactionHash || isVerifying}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold py-4 rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isVerifying ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                    Verifying Payment...
                  </span>
                ) : (
                  'Verify Payment'
                )}
              </motion.button>
            </div>
          </div>

          {/* Instructions */}
          <div className="mt-8 bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
            <h3 className="text-blue-400 font-semibold mb-2">📝 Payment Instructions:</h3>
            <ol className="text-sm text-gray-300 space-y-1">
              <li>1. Send exactly <strong>${subscription.amount} USDT</strong> to the address above</li>
              <li>2. Use <strong>TRC-20 network</strong> (Tron) for lowest fees</li>
              <li>3. Copy the transaction hash after sending</li>
              <li>4. Paste it above and click "Verify Payment"</li>
              <li>5. Your subscription will be activated automatically</li>
            </ol>
          </div>

          {/* Support */}
          <div className="mt-6 text-center">
            <p className="text-gray-400 text-sm">
              Need help? Contact our support team
            </p>
            <a 
              href="mailto:support@vonscalper.com" 
              className="text-blue-400 hover:text-blue-300 transition-colors"
            >
              support@vonscalper.com
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default USDTPayment;